import { Button } from '@1trading/react/components/ui/button';
import { GradientIcon } from '@1trading/react/components/ui/gradient-icon';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { ClockFading, EditIcon, UserCheck, UserX } from 'lucide-react';
import type React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, Navigate } from 'react-router-dom';

import LogoutButton from '@/components/ui/logout-button';
import { PageHeading, PageHeadingDescription, PageHeadingTitle } from '@/components/ui/page-heading';
import type { LdFeatureFlags } from '@/config/features';
import Verification from '@/features/verification/components';
import { SumsubReviewAnswer, SumsubReviewRejectType, SumsubReviewStatus } from '@/features/verification/constants/sumsub';
import type { WebSDKEventsApplicantStatusChanged } from '@/features/verification/types/sumsub';
import { useProfileNationalClientIdentifier } from '@/hooks/queries/use-profile-national-client-identifier';
import { useProfileVerificationInformation } from '@/hooks/queries/use-profile-verification-information';
import { useProfileVerificationSumsubResult } from '@/hooks/queries/use-profile-verification-information-sumsub-result';
import { useProfileVerificationInformationSupportingInformation } from '@/hooks/queries/use-profile-verification-information-supporting-information';
import { useUserAttributes } from '@/hooks/queries/use-user-attributes';
import BaseLayout from '@/layouts/base';
import { WORKFLOW_LEVEL_IDENTIFIER } from '@/services/api/profile';
import { getNationalClientIdentifierTypeLabel } from '@/utils/national-client-identifier';
import { isInstitutionalUserSegment } from '@/utils/user-segment';
import LoadingPage from './loading';

const VerificationStatusLayout = ({ children }: React.PropsWithChildren) => {
  return (
    <BaseLayout className="-md:justify-center space-y-8" size="sm">
      {children}
    </BaseLayout>
  );
};
VerificationStatusLayout.displayName = 'VerificationStatusLayout';

const VerificationStatusNciEditLink = () => {
  const { t } = useTranslation();

  return (
    <Button className="gap-2 h-auto p-0 align-middle text-base text-primary" variant="link" asChild>
      <Link to="/onboarding/verification/status/verification-information">
        <EditIcon className="size-4" />
        {t('fundamentals.edit')}
      </Link>
    </Button>
  );
};
VerificationStatusNciEditLink.displayName = 'VerificationStatusNciEditLink';

const VerificationStatusNciDescription = () => {
  const { data: nationalClientIdentifier } = useProfileNationalClientIdentifier();
  const { t } = useTranslation();

  return (
    <div className="space-y-4 md:pr-[20%]">
      <p>{t('verification.description')}</p>
      {nationalClientIdentifier ? (
        <p>
          <Trans
            i18nKey="verificationStatus.descriptionNationalClientIdentifier"
            values={{ identifierType: getNationalClientIdentifierTypeLabel(nationalClientIdentifier.answers.identifierType, nationalClientIdentifier.answers.country) }}
            components={[<strong key="identifierType" />, <VerificationStatusNciEditLink key="editLink" />]}
          />
        </p>
      ) : null}
    </div>
  );
};
VerificationStatusNciDescription.displayName = 'VerificationStatusNciDescription';

const VerificationReviewStatusPending = () => {
  const { data: userAttributes } = useUserAttributes();
  const { t } = useTranslation();
  useProfileVerificationSumsubResult(true);
  const { segment: userSegment } = userAttributes ?? {};
  const isInstitutional = isInstitutionalUserSegment(userSegment);

  return (
    <VerificationStatusLayout>
      <GradientIcon className="size-32 mx-auto" strokeWidth={1.5}>
        <ClockFading />
      </GradientIcon>
      <PageHeading centered>
        <PageHeadingTitle>{t('verificationStatus.pending.title')}</PageHeadingTitle>
        <PageHeadingDescription>{t(isInstitutional ? 'verificationStatus.institution.pending.description' : 'verificationStatus.pending.description')}</PageHeadingDescription>
      </PageHeading>
      <footer className="flex flex-col gap-4">
        <Button size="lg" variant="primary" asChild>
          <a href={import.meta.env.VITE_APP_EXCHANGE_URL}>{t('buttons.exchange')}</a>
        </Button>
        <Button size="lg" variant="outline" asChild>
          <a href={import.meta.env.VITE_APP_SUPPORT_NEW_REQUEST_URL}>{t('contactSupport')}</a>
        </Button>
      </footer>
    </VerificationStatusLayout>
  );
};
VerificationReviewStatusPending.displayName = 'VerificationReviewStatusPending';

const VerificationStatusSuccess = () => {
  const { data: userAttributes } = useUserAttributes();
  const { segment: userSegment } = userAttributes ?? {};
  const isInstitutional = isInstitutionalUserSegment(userSegment);
  const { t } = useTranslation();

  return (
    <VerificationStatusLayout>
      <GradientIcon className="size-32 mx-auto" strokeWidth={1.5}>
        <UserCheck />
      </GradientIcon>
      <PageHeading centered>
        <PageHeadingTitle>{t(isInstitutional ? 'verificationStatus.institution.success.title' : 'verificationStatus.success.title')}</PageHeadingTitle>
        <PageHeadingDescription>{t(isInstitutional ? 'verificationStatus.institution.success.description' : 'verificationStatus.success.description')}</PageHeadingDescription>
      </PageHeading>
      <Button size="lg" variant="primary" asChild>
        <a href={import.meta.env.VITE_APP_EXCHANGE_URL}>{t('buttons.exchange')}</a>
      </Button>
    </VerificationStatusLayout>
  );
};
VerificationStatusSuccess.displayName = 'VerificationStatusSuccess';

const VerificationStatusFailed = () => {
  const { t } = useTranslation();

  return (
    <VerificationStatusLayout>
      <GradientIcon className="size-32 mx-auto" strokeWidth={1.5}>
        <UserX />
      </GradientIcon>
      <PageHeading centered>
        <PageHeadingTitle>{t('verificationStatus.failed.title')}</PageHeadingTitle>
        <PageHeadingDescription>{t('verificationStatus.failed.description')}</PageHeadingDescription>
      </PageHeading>
      <footer className="flex flex-col gap-4">
        <Button size="lg" variant="primary" asChild>
          <a href={import.meta.env.VITE_APP_SUPPORT_NEW_REQUEST_URL}>{t('contactSupport')}</a>
        </Button>
        <LogoutButton size="lg" variant="outline" />
      </footer>
    </VerificationStatusLayout>
  );
};
VerificationStatusFailed.displayName = 'VerificationStatusFailed';

const VerificationStatusPage = () => {
  const { isLoading: isLoadingVerificationInformation, data: verificationInformation, invalidate: invalidateVerificationInformation } = useProfileVerificationInformation();
  const isWorkflowVerificationLevel = !!verificationInformation?.verification?.level?.includes(WORKFLOW_LEVEL_IDENTIFIER);
  const { invalidate: invalidateVerificationSupportingInformation } = useProfileVerificationInformationSupportingInformation(isWorkflowVerificationLevel);
  const { isLoading: isLoadingVerificationSumsubResult, data: verificationSumsubResult, set: setVerificationSumsubResult } = useProfileVerificationSumsubResult();
  const { nciPriorityFlowEnabled } = useFlags<LdFeatureFlags>();
  const { isLoading: isLoadingNationalClientIdentifier, data: nationalClientIdentifier } = useProfileNationalClientIdentifier(nciPriorityFlowEnabled);
  const { isLoading: isLoadingUserAttributes } = useUserAttributes();
  const isLoading = isLoadingVerificationInformation || isLoadingVerificationSumsubResult || isLoadingNationalClientIdentifier || isLoadingUserAttributes;
  const { status, result, rejectType } = verificationSumsubResult ?? {};

  const handleChange = (data: WebSDKEventsApplicantStatusChanged) => {
    invalidateVerificationInformation();
    invalidateVerificationSupportingInformation();
    setVerificationSumsubResult({
      status: data.reviewStatus as SumsubReviewStatus,
      result: data.reviewResult?.reviewAnswer,
      rejectType: data.reviewResult?.reviewRejectType,
    });
  };

  if (isLoading) return <LoadingPage />;

  if (nciPriorityFlowEnabled && !nationalClientIdentifier) {
    return <Navigate to="/onboarding/verification/status/verification-information" replace />;
  }

  const VerificationComponent = (
    <Verification
      description={nationalClientIdentifier ? <VerificationStatusNciDescription /> : undefined}
      layoutProps={{ currentStep: null }}
      prev={nciPriorityFlowEnabled ? '/onboarding/verification/status/verification-information' : undefined}
      showPrevButton={false}
      onChange={handleChange}
    />
  );

  switch (status) {
    case SumsubReviewStatus.PENDING:
    case SumsubReviewStatus.PRE_CHECKED:
    case SumsubReviewStatus.QUEUED:
    case SumsubReviewStatus.ON_HOLD:
    // Note: We're intentionally excluding AWAITING_USER here so that we default to the verification component (Sumsub) incase the actions required are actionable by the user
    case SumsubReviewStatus.AWAITING_SERVICE:
      return <VerificationReviewStatusPending />;
    case SumsubReviewStatus.COMPLETED: {
      switch (result) {
        case SumsubReviewAnswer.GREEN:
          return <VerificationStatusSuccess />;
        case SumsubReviewAnswer.RED:
          if (rejectType === SumsubReviewRejectType.FINAL) return <VerificationStatusFailed />;
      }

      return VerificationComponent;
    }
    default:
      return VerificationComponent;
  }
};
VerificationStatusPage.displayName = 'VerificationStatusPage';

export default VerificationStatusPage;
